import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Building2, Briefcase } from "lucide-react";
import SEO from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { facility1, facility2, facility3, facility4, facility5, heroImage, heroImageSrcSet, facilitySrcSet } from "@/lib/cdn-images";
import { organizationSchema, donateActionSchema } from "@/lib/organization-schema";
const HERO_IMAGE = heroImage;

const GIVE_URL = "https://give.abbashouse.org";

const GOAL = 3_000_000;

const stats = [
  {
    Icon: TrendingUp,
    number: "18%",
    label: "Rise in Homelessness",
    desc: "In 2024 alone, homelessness increased by 18% — a crisis that demands action now.",
  },
  {
    Icon: Building2,
    number: "$∞",
    label: "Rising Rents",
    desc: "Private equity continues to increase rents — displacing long-standing tenants from their homes.",
  },
  {
    Icon: Briefcase,
    number: "0",
    label: "Jobs Without an Address",
    desc: "Many who are displaced face unemployment — without a mailing address, work is nearly impossible.",
  },
];

const impactCards = [
  {
    amount: "$38/mo",
    detail: "× 20 donors",
    outcome: "= 1 person housed",
    desc: "Just $38 a month from 20 generous donors will provide safe housing for one individual when we open many years from now.",
  },
  {
    amount: "$750/mo",
    detail: "per person",
    outcome: "= A life restored",
    desc: "Will fully cover housing, meals, clothing, healthcare assistance, and transition support for one member when we reach our long-term goal.",
  },
  {
    amount: "$3M",
    detail: "fundraising goal",
    outcome: "= Our first facility",
    desc: "Help us reach our overall long-term goal of $3 million to open our first Community Housing Center and change hundreds of lives.",
  },
];

const facilityImages = [
  { src: facility1, alt: "Abba's House Community Housing Exterior" },
  { src: facility2, alt: "Private Pod Living Space" },
  { src: facility3, alt: "Shared Gym Amenities" },
  { src: facility4, alt: "Community Recreation Room" },
  { src: facility5, alt: "Community Cafeteria & Kitchen" },
];

export default function Home() {
  const [raised, setRaised] = useState(0);

  useEffect(() => {
    // Defer the fundraising fetch until after LCP so it never blocks the hero paint.
    // requestIdleCallback (with a setTimeout fallback) waits for the browser to be idle.
    const fetchRaised = async () => {
      try {
        const { data, error } = await supabase
          .from("site_settings" as any)
          .select("value")
          .eq("key", "fundraising_raised")
          .maybeSingle();
        if (error) return; // silently ignore — non-critical
        if (data) setRaised(parseFloat((data as any).value) || 0);
      } catch {
        // network/abort — non-critical, do not log
      }
    };
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    const handle = ric
      ? ric(() => fetchRaised(), { timeout: 2500 })
      : window.setTimeout(fetchRaised, 1500);
    return () => {
      const cic = (window as any).cancelIdleCallback as ((id: number) => void) | undefined;
      if (ric && cic) cic(handle as number);
      else clearTimeout(handle as number);
    };
  }, []);

  return (
    <main>
      <SEO
        title="A Home For Everyone"
        description="Abba's House Communities is a nonprofit Christian ministry raising funds to build Community Housing Centers — free 90-day transition programs, job placement, healthcare, and more. Give today."
        canonical="https://abbashouse.org/"
        jsonLd={[organizationSchema, donateActionSchema()]}
      />
      <section className="relative min-h-[90vh] flex items-center justify-center text-primary-foreground overflow-hidden">
        {/* Background image — visible but text remains readable */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            srcSet={heroImageSrcSet}
            sizes="100vw"
            alt=""
            width={1280}
            height={720}
            className="w-full h-full object-cover"
            aria-hidden="true"
            // @ts-ignore – React 18 doesn't type fetchPriority yet
            fetchpriority="high"
          />
          {/* Dark overlay — lightened so the photo shows through */}
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center py-24">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase opacity-60 mb-6">
            Matthew 25:35–40
          </p>
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-none mb-6">
            A Home For Everyone
          </h1>
          <p className="text-xl sm:text-2xl opacity-70 mb-10 max-w-2xl mx-auto leading-relaxed">
            Building hope for the homeless — raising funds to build Community Housing Centers so no one sleeps on the streets again.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={GIVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 rounded-full bg-primary-foreground text-primary text-base font-bold tracking-wide hover:opacity-90 transition-opacity w-full sm:w-auto justify-center"
            >
              Give Now
            </a>
            <Link
              to="/about"
              aria-label="Learn more about Abba's House Communities"
              className="inline-flex items-center px-8 py-4 rounded-full border-2 border-primary-foreground text-primary-foreground text-base font-semibold tracking-wide hover:bg-primary-foreground hover:text-primary transition-colors w-full sm:w-auto justify-center"
            >
              Learn About Our Mission
            </Link>
          </div>
        </div>
      </section>

      {/* The Crisis — Stats */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">The Crisis Is Real</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              The numbers don't lie. Homelessness is growing — and the cycle is brutal.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="border border-border rounded-2xl p-8 flex flex-col gap-4 hover:border-foreground transition-colors">
                <stat.Icon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                <div className="text-6xl font-black tracking-tighter text-foreground">{stat.number}</div>
                <h3 className="text-lg font-bold">{stat.label}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fundraising Progress Bar */}
      {(() => {
        const pct = Math.min((raised / GOAL) * 100, 100);
        const fmt = (n: number) =>
          n >= 1_000_000
            ? `$${(n / 1_000_000).toFixed(2)}M`
            : `$${n.toLocaleString("en-US")}`;
        return (
          <section className="py-16 md:py-20 bg-secondary">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
                  First Facility Fund
                </h2>
                <p className="text-muted-foreground text-base max-w-lg mx-auto">
                  Help us reach our long-term goal of <strong className="text-foreground">$3,000,000</strong> to open our first Community Housing Center. Every dollar counts.
                </p>
              </div>
              <div className="bg-background rounded-2xl p-8 md:p-10 shadow-sm border border-border">
                {/* Numbers row */}
                <div className="flex flex-row justify-between items-end gap-4 mb-6">
                  <div>
                    <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Raised So Far</p>
                    <p className="text-4xl md:text-5xl font-black tracking-tight">{fmt(raised)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Our Goal</p>
                    <p className="text-4xl md:text-5xl font-black tracking-tight text-muted-foreground">{fmt(GOAL)}</p>
                  </div>
                </div>
                {/* Bar */}
                <div className="relative w-full h-5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-700"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {/* Pct label */}
                <div className="flex justify-between items-center mt-3">
                  <p className="text-sm font-bold">{pct.toFixed(1)}% funded</p>
                  <p className="text-sm text-muted-foreground">{fmt(GOAL - raised)} remaining</p>
                </div>
                <div className="mt-8 text-center">
                  <a
                    href={GIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-8 py-4 rounded-full bg-primary text-primary-foreground text-base font-bold tracking-wide hover:opacity-80 transition-opacity"
                  >
                    Give Toward This Goal
                  </a>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* How Your Gift Helps */}
      <section className="py-12 md:py-16 bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">How Your Gift Helps</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Every dollar is a step toward a door that opens for someone.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impactCards.map((card) => (
              <div key={card.amount} className="bg-primary text-primary-foreground rounded-2xl p-8 flex flex-col gap-4">
                <div className="text-5xl font-black tracking-tighter">{card.amount}</div>
                <div className="text-sm opacity-60 font-medium">{card.detail}</div>
                <div className="text-xl font-bold border-t border-primary-foreground/20 pt-4">{card.outcome}</div>
                <p className="text-sm opacity-70 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-[0.3em] uppercase text-muted-foreground mb-4">Our Programs</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">A Path for Every Person</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Wherever you are — we have a program designed for you.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
            {[
              { num: "01", title: "90-Day Transition Program", desc: "Will provide free housing, meals, clothing, healthcare assistance, documentation, job placement, and case management for those in crisis once our first center opens.", href: "/programs#transition" },
              { num: "02", title: "Independent Membership", desc: "Graduates of our 90-day program will transition to a $750/mo plan covering their pod, reduced-priced meals, optional continued support, and a pay-it-forward contribution for someone else.", href: "/programs#membership" },
              { num: "03", title: "Direct Entry Program", desc: "Already employed but priced out? When our center opens, you'll be able to move in immediately with a private pod, full access to laundry facilities, private showers, gym, business center, and community lounges — plus equal access to Abba's Care healthcare benevolent fund.", href: "/programs#direct-entry" },
              { num: "04", title: "Abba's Second Chance", desc: "We plan to work with courts and Governor's Pardon Boards to help those with nonviolent records earn expungements and pardons.", href: "/programs#second-chance" },
            ].map((p) => (
              <a
                key={p.num}
                href={p.href}
                className="group border border-border rounded-2xl p-7 hover:border-foreground transition-colors flex flex-col gap-4"
              >
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">{p.num}</span>
                <h3 className="text-xl font-black tracking-tight group-hover:text-primary transition-colors">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{p.desc}</p>
                <span className="text-sm font-semibold text-primary">Learn more →</span>
              </a>
            ))}
          </div>
          <div className="text-center">
            <a href="/programs" className="inline-flex items-center px-8 py-4 rounded-full bg-primary text-primary-foreground text-base font-bold tracking-wide hover:opacity-80 transition-opacity">
              View All Programs
            </a>
          </div>
        </div>
      </section>

      {/* Facility Concept Gallery */}
      <section className="py-20 md:py-28 bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-[0.3em] uppercase text-muted-foreground mb-4">Our Vision</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">What Our Centers Will Look Like</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              We are actively fundraising to open our first Community Housing Center. These images represent the concept — safe, dignified, community-centered spaces we are building toward.
            </p>
            <p className="text-sm text-muted-foreground italic mt-3 max-w-xl mx-auto">
              *Disclaimer: The images shown are conceptual renderings only and do not represent a final design. Actual facilities may differ in appearance.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {facilityImages.map((img, i) => {
              const spans = i === 0;
              const sizes = spans
                ? "(min-width: 1024px) 854px, (min-width: 640px) 100vw, calc(100vw - 32px)"
                : "(min-width: 1024px) 427px, (min-width: 640px) 50vw, calc(100vw - 32px)";
              return (
                <div
                  key={i}
                  className={`rounded-2xl overflow-hidden ${spans ? "sm:col-span-2 lg:col-span-2" : ""}`}
                >
                  <img
                    src={img.src}
                    srcSet={facilitySrcSet(img.src)}
                    sizes={sizes}
                    alt={img.alt}
                    width={1280}
                    height={720}
                    className="w-full h-64 sm:h-72 object-cover hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Scripture Banner */}
      <section className="bg-primary text-primary-foreground py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase opacity-50 mb-8">Our Foundation</p>
          <blockquote className="text-2xl sm:text-3xl md:text-4xl font-light italic leading-relaxed opacity-90 mb-8">
            "Protect the rights of the poor and the orphans. Be fair to the needy and the helpless. Rescue the poor and helpless. Save them from the power of the wicked."
          </blockquote>
          <cite className="text-base font-bold tracking-widest opacity-50 not-italic">— PSALMS 82:3–4 (ERV)</cite>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight leading-tight mb-6">
            Will You Be the Reason Someone Has A Home?
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
            Your gift — no matter the size — moves us closer to opening our doors. Partner with us and be the change.
          </p>
          <a
            href={GIVE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-10 py-5 rounded-full bg-primary text-primary-foreground text-lg font-bold tracking-wide hover:opacity-80 transition-opacity"
          >
            Give Now — Every Dollar Counts
          </a>
        </div>
      </section>
    </main>
  );
}